/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useContext, useEffect, useState } from 'react';
import { useI18n } from '@oneaudi/i18n-context';
import { Popover } from '@oneaudi/unified-web-components';
import type { AudiHeaderDealerProps } from '../../interfaces/header-components.interfaces';
import { StyledIconButton } from './AudiHeaderDealerContextStyles';
import { messages } from '../../i18n/messages';
import { DealerContext } from '../../context';
import { DEALER_CONTEXT_UNSUBSCRIBE, NAVIGATION_CLASSNAME } from '../../utils/globals';
import AudiHeaderAdditionalDealerDataAlpha from './AudiHeaderAdditionalDealerData_alpha';
import { truncate } from '../../utils/string-utils';
import styled from 'styled-components';

const StyledButtonContainer = styled.div`
  cursor: pointer;
`;

const AudiHeaderDealerContextAlpha: React.FC<AudiHeaderDealerProps> = ({
  isMobileView = false,
  onClickDealerContext,
  showDealerAdditionalData,
  showDealerSearch,
}) => {
  const { dealerData } = useContext(DealerContext);
  const dealerButtonLabelAria = useI18n(messages.dealer_information);
  const dealerButtonLabelAriaClose = useI18n(messages.dealer_information_close);
  const [isOpen, setIsOpen] = useState(false);

  const buttonLabel = !isMobileView
    ? truncate(dealerData?.additionalData?.displayName || dealerData?.name)
    : dealerData?.additionalData?.displayName || dealerData?.name || 'Name not found';

  useEffect(() => {
    if (showDealerAdditionalData && !isOpen) {
      const currentScrollY = window.scrollY;
      const isScrolled = currentScrollY > 1;

      if (isScrolled) {
        const handleScrollEnd = () => {
          const scrollY = window.scrollY;

          if (scrollY === 0) {
            window.removeEventListener('scroll', handleScrollEnd);
            setIsOpen(true);
          }
        };

        window.addEventListener('scroll', handleScrollEnd);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setIsOpen(true);
      }
    } else if (!showDealerAdditionalData && isOpen) {
      setIsOpen(false);
    }
  }, [showDealerAdditionalData, isOpen]);

  const handleToggle = () => {
    const newState = !isOpen;
    const currentScrollY = window.scrollY;
    const isScrolled = currentScrollY > 1;

    if (newState && isScrolled) {
      // Opening and page is scrolled - scroll to top first
      const handleScrollEnd = () => {
        const scrollY = window.scrollY;

        if (scrollY === 0) {
          window.removeEventListener('scroll', handleScrollEnd);
          setIsOpen(true);
          onClickDealerContext();
        }
      };

      window.addEventListener('scroll', handleScrollEnd);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Closing or already at top - toggle immediately
      setIsOpen(newState);
      onClickDealerContext();
    }
  };

  if (dealerData?.toString() === DEALER_CONTEXT_UNSUBSCRIBE) {
    return null;
  }

  return (
    <>
      {!isMobileView && dealerData ? (
        <Popover
          content={
            <AudiHeaderAdditionalDealerDataAlpha
              open={isOpen}
              isMobileView={isMobileView}
              refDealerData={null}
              showDealerSearch={showDealerSearch}
            />
          }
          data-testid="dealerContext-test-id"
          id="dealerContext"
          onClose={handleToggle}
          triggerElement={
            <StyledButtonContainer onClick={handleToggle}>
              <StyledIconButton
                aria-label={isOpen ? dealerButtonLabelAriaClose : dealerButtonLabelAria}
                data-testid="test-id"
                icon={isOpen ? 'cancel' : 'poi-active'}
                iconPosition="leading"
                onClick={handleToggle}
                size="md"
                title={buttonLabel}
                type="button"
                variant="tertiary"
                className={NAVIGATION_CLASSNAME}
                // @ts-expect-error
                selected={isOpen}
              >
                {buttonLabel}
              </StyledIconButton>
            </StyledButtonContainer>
          }
          withTip={false}
          open={isOpen}
          alignment="center"
          placement="top"
        />
      ) : null}
    </>
  );
};

export default AudiHeaderDealerContextAlpha;
